import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../firebaseConfig';

export default function MuultiSpacesCarousel({
                                                 title = 'Explore Public Spaces',
                                                 limitCount = 24,
                                                 withArrows = true,
                                                 snap = 'center',         // 'center' | 'start'
                                                 itemWidth = 280,         // px
                                                 autoScroll = false,      // enable marquee auto-scroll
                                                 autoDirection = 'right', // 'right' | 'left'
                                                 autoSpeed = 40,          // px per second
                                                 pauseOnHover = true,     // pause auto-scroll on hover
                                                 requireComponents = true // only show users with components by default
                                             }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const scrollerRef = useRef(null);
    const dragging = useRef(false);
    const startX = useRef(0);
    const startLeft = useRef(0);
    const pausedRef = useRef(false); // auto-scroll pause gate

    // Fetch spaces (reuse directory-style filter)
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const q = query(
                    collection(db, 'users'),
                    where('muultiName', '!=', '')
                );


                const snap = await getDocs(q);
                const list = [];

                snap.forEach(doc => {
                    const d = doc.data();
                    const hasMuultiName =
                        typeof d.muultiName === 'string' && d.muultiName.trim() !== '';

                    const hasDescription =
                        typeof d.pageDescription === 'string' &&
                        d.pageDescription.trim() !== '';

                    const hasPanels =
                        Array.isArray(d.components) && d.components.length > 0;

                    const hasPublicKey = !!d.publicKey;

// Must always have a name + public key
                    if (!hasMuultiName || !hasPublicKey) return;

// Must have *some* content
                    if (!hasDescription && !hasPanels) return;

// Optional stricter filter
                    if (requireComponents && !hasPanels) return;


                    list.push({
                        id: doc.id,
                        muultiName: d.muultiName,
                        pageTitle: d.pageTitle || d.muultiName,
                        pageDescription: d.pageDescription || '',
                        profilePicUrl: d.profilePicUrl || ''
                    });
                });

                // Client-side alpha sort by title/muultiName
                list.sort((a, b) =>
                    (a.pageTitle || a.muultiName || '').localeCompare(
                        b.pageTitle || b.muultiName || '',
                        'en',
                        { sensitivity: 'base' }
                    )
                );

                setItems(list);
            } catch (e) {
                console.error('Carousel load failed', e);
                setItems([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [limitCount, requireComponents]);

// Drag to scroll: ONLY allow touch swipe (mobile/tablet). Disable mouse drag on desktop.
    const isTouchEvent = e =>
        (typeof e?.pointerType === 'string' && e.pointerType === 'touch') ||
        (typeof e?.pointerType === 'number' && e.pointerType === 2); // Safari oddities sometimes

    const onPointerDown = e => {
        const el = scrollerRef.current;
        if (!el) return;

        // Ignore mouse/stylus; we only want touch swipe
        if (!isTouchEvent(e)) return;

        dragging.current = true;
        startX.current = e.pageX - el.getBoundingClientRect().left;
        startLeft.current = el.scrollLeft;
        el.classList.add('dragging');
        pausedRef.current = true;
    };

    const onPointerMove = e => {
        const el = scrollerRef.current;
        if (!el || !dragging.current) return;

        // Only touch
        if (!isTouchEvent(e)) return;

        // prevent page scroll while swiping horizontally
        e.preventDefault();

        const x = e.pageX - el.getBoundingClientRect().left;
        el.scrollLeft = startLeft.current - (x - startX.current) * 1.1;
    };

    const endDrag = () => {
        const el = scrollerRef.current;
        if (!el) return;
        dragging.current = false;
        el.classList.remove('dragging');
        pausedRef.current = false;
    };


    // Pause auto-scroll on focus/hover if enabled
    const onFocus = () => {
        pausedRef.current = true;
    };
    const onBlur = () => {
        pausedRef.current = false;
    };
    const onMouseEnter = () => {
        if (pauseOnHover) pausedRef.current = true;
    };
    const onMouseLeave = () => {
        if (pauseOnHover) pausedRef.current = false;
    };

    // Keyboard arrows to scroll when focused
    const onKeyDown = e => {
        const el = scrollerRef.current;
        if (!el) return;
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            el.scrollBy({ left: itemWidth + 16, behavior: 'smooth' });
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            el.scrollBy({ left: -(itemWidth + 16), behavior: 'smooth' });
        }
    };

    // Auto-scroll loop (marquee)
    useEffect(() => {
        if (!autoScroll) return;

        const el = scrollerRef.current;
        if (!el) return;

        // Respect reduced motion
        if (
            window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
            return;
        }

        let rafId;
        let last = performance.now();

        const step = now => {
            rafId = requestAnimationFrame(step);
            if (pausedRef.current || dragging.current) {
                last = now;
                return;
            }

            const dt = (now - last) / 1000;
            last = now;

            const delta =
                autoSpeed * dt * (autoDirection === 'left' ? -1 : 1);
            el.scrollLeft += delta;

            // seamless wrap (we render items twice)
            const half = el.scrollWidth / 2;
            if (half > 0) {
                if (el.scrollLeft >= half) {
                    el.scrollLeft -= half;
                } else if (el.scrollLeft < 0) {
                    el.scrollLeft += half;
                }
            }
        };

        rafId = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafId);
    }, [autoScroll, autoDirection, autoSpeed]);

    // Disable snap when auto-scrolling to avoid fights
    const snapClass = useMemo(() => {
        if (autoScroll) return 'no-snap';
        return snap === 'start' ? 'snap-start' : 'snap-center';
    }, [snap, autoScroll]);

    // Duplicate for seamless loop in auto mode
    const renderItems = autoScroll ? [...items, ...items] : items;

    return (
        <div className="muulti-carousel">
            <div className="muulti-carousel-head">
                <h3>{title}</h3>
            </div>

            <div className="muulti-carousel-body">
                {withArrows && !autoScroll && (
                    <>
                        <button
                            className="carousel-side-btn left"
                            onClick={() =>
                                scrollerRef.current?.scrollBy({
                                    left: -(itemWidth + 16),
                                    behavior: 'smooth'
                                })
                            }
                            aria-label="Previous"
                            type="button"
                        >
                            ‹
                        </button>

                        <button
                            className="carousel-side-btn right"
                            onClick={() =>
                                scrollerRef.current?.scrollBy({
                                    left: itemWidth + 16,
                                    behavior: 'smooth'
                                })
                            }
                            aria-label="Next"
                            type="button"
                        >
                            ›
                        </button>
                    </>
                )}

                <div
                    className={`muulti-carousel-track ${snapClass} is-transparent`}
                    ref={scrollerRef}
                    role="listbox"
                    tabIndex={0}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onPointerDown={onPointerDown}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onPointerMove={onPointerMove}
                    onKeyDown={onKeyDown}
                    style={{ '--card-w': `${itemWidth}px` }}
                    aria-label="Muulti spaces carousel"
                >
                    {loading && <div className="carousel-empty">Loading…</div>}
                    {!loading && items.length === 0 && (
                        <div className="carousel-empty">No spaces found.</div>
                    )}

                    {renderItems.map((item, idx) => {
                        const name = item.pageTitle || item.muultiName || 'Untitled';
                        const href = item.muultiName ? `/${item.muultiName}` : '#';

                        return (
                            <div key={`${item.id}-${idx}`} className="muulti-card" role="option">
                                <Link to={href} className="muulti-card-inner">
                                    {item.profilePicUrl ? (
                                        <img
                                            className="muulti-card-avatar"
                                            src={item.profilePicUrl}
                                            alt={name}
                                        />
                                    ) : (
                                        <div className="muulti-card-avatar placeholder">
                                        <span className="avatar-initials">
                                            {name.charAt(0).toUpperCase()}
                                        </span>
                                        </div>
                                    )}
                                    <div className="muulti-card-meta">
                                        <div className="muulti-card-title">{name}</div>
                                        {item.pageDescription && (
                                            <div className="muulti-card-desc">{item.pageDescription}</div>
                                        )}
                                    </div>
                                </Link>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

}
